import React, {useCallback, useEffect, useState} from 'react';
import {Card, CardBody, Col, Row} from "reactstrap";
import {useTranslation} from "react-i18next";
import {toast} from "react-toastify";
import ButtonSpinner from "../../../components/ButtonSpinner";
import RemoveModal from "../../../components/RemoveForm";
import {getOAuthGrants, OAuthGrantType, revokeOAuthGrant} from "../../../api";
import {connectorHandlerCode} from "../../../constants/ConnectorHandler";
import {getHandlerIconByCode} from "../../../helpers/functions";

// Logos by the host m9s-id verified from the client's redirect URIs — never by
// the app's name, which whoever registered the client chose freely.
const HANDLER_BY_VERIFIED_HOST: Record<string, string> = {
    'claude.ai': connectorHandlerCode.CONNECTOR_HANDLER_ANTHROPIC_CLAUDE,
    'chatgpt.com': connectorHandlerCode.CONNECTOR_HANDLER_CHAT_GPT,
    // Gemini (formerly Bard): the consumer app's Google account-linking relay
    // and Gemini Enterprise. Perplexity has no logo yet and shows its name.
    'oauth-redirect.googleusercontent.com': connectorHandlerCode.CONNECTOR_HANDLER_GOOGLE_BARD,
    'vertexaisearch.cloud.google.com': connectorHandlerCode.CONNECTOR_HANDLER_GOOGLE_BARD,
};

const logoOf = (grant: OAuthGrantType): string | null => {
    const code = grant.verifiedHost ? HANDLER_BY_VERIFIED_HOST[grant.verifiedHost] : undefined;

    return code ? getHandlerIconByCode(code) : null;
};

/**
 * Apps connected through OAuth (claude.ai, ChatGPT and other MCP clients), with
 * a way to disconnect each. Disconnecting revokes the app's tokens in m9s-id;
 * changing the password disconnects all of them.
 */
const ConnectedApps = () => {

    const {t, i18n} = useTranslation(['auth', 'common']);
    const [grants, setGrants] = useState<OAuthGrantType[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [selected, setSelected] = useState<OAuthGrantType | null>(null);

    const load = useCallback(() => {
        setIsLoading(true);
        getOAuthGrants()
            .then((response: any) => setGrants(response?.success === true ? (response.items || []) : []))
            .catch(() => setGrants([]))
            .finally(() => setIsLoading(false));
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const disconnect = useCallback((grant: OAuthGrantType) => {
        revokeOAuthGrant(grant.clientId)
            .then(() => {
                toast.success(t('auth:oauthDisconnected', {client: grant.name}));
                setGrants(current => current.filter(item => item.clientId !== grant.clientId));
            })
            .catch(() => toast.error(t('auth:oauthDisconnectFailed')))
            .finally(() => setSelected(null));
    }, [t]);

    if (isLoading) {
        return <div className="text-center text-muted py-4"><ButtonSpinner/></div>;
    }

    if (grants.length < 1) {
        return <p className="text-muted mb-0">{t('auth:oauthNoConnectedApps')}</p>;
    }

    return (
        <>
            <Row>
                {grants.map(grant => {
                    const logo = logoOf(grant);

                    return (
                        <Col xxl={6} sm={6} key={grant.clientId}>
                            <Card className={"card-height-100 card-animate profile-project-card" + (grant.verifiedHost ? ' profile-project-success' : ' profile-project-warning')}>
                                <CardBody>
                                    <div className="d-flex flex-shrink-0 position-relative align-items-center">
                                        <div className="avatar-sm me-3 d-flex flex-shrink-0 position-relative align-items-center">
                                            {logo !== null ?
                                                <img src={logo} className="rounded-circle img-fluid" alt={grant.name}/>
                                                :
                                                <span className="avatar-title rounded-circle">
                                                    <i className="ri-plug-line"/>
                                                </span>
                                            }
                                        </div>
                                        <div className="flex-1 justify-content-center flex-column d-flex mw-0">
                                            <div className="text-truncate">{grant.name}</div>
                                            {grant.verifiedHost &&
                                                <small className="text-muted text-truncate">{grant.verifiedHost}</small>
                                            }
                                            <small className="text-muted text-truncate">
                                                {t('auth:oauthConnectedUntil', {date: new Date(grant.connectedUntil).toLocaleDateString(i18n.language)})}
                                            </small>
                                            {!grant.verifiedHost &&
                                                <div className="d-flex flex-row mt-1">
                                                    <span className="d-flex text-truncate badge bg-light text-warning">{t('auth:oauthUnverifiedBadge')}</span>
                                                </div>
                                            }
                                        </div>
                                        <div className="d-flex flex-shrink-0 align-items-center">
                                            <button
                                                onClick={() => setSelected(grant)}
                                                title={t('auth:oauthDisconnect')}
                                                className="btn btn-outline-danger btn-icon waves-effect waves-light ms-2">
                                                <i className="ri-delete-bin-5-line"/>
                                            </button>
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    );
                })}
            </Row>

            <RemoveModal
                isOpen={selected !== null}
                removeItem={selected}
                onRemove={disconnect}
                handleClose={() => setSelected(null)}
                modalTitle={t('auth:oauthDisconnect')}
            />
        </>
    );
};

export default ConnectedApps;
